// React hooks imports
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Custom hooks imports
import { useNavigateWithSameKey } from "../../../../../../../custom-hooks/navigateWithKey";

//MUI Components imports
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2,
  Container,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Refresh as ResetIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";

// Local images imports
import { ReactComponent as SeleniumIcon } from "../../../../../../../images/test-automation/tools_SeleniumIcon.svg";
import { ReactComponent as PlaywrightIcon } from "../../../../../../../images/test-automation/tools_PlaywriteIcon.svg";
import { ReactComponent as CypressIcon } from "../../../../../../../images/test-automation/tools_CypressIcon.svg";
import { ReactComponent as RobotIcon } from "../../../../../../../images/test-automation/tools_robotframeworkIcon.svg";
import { ReactComponent as CucumberIcon } from "../../../../../../../images/test-automation/framework_CucumberIcon.svg";
import { ReactComponent as SpecFlowIcon } from "../../../../../../../images/test-automation/framework_specflowIcon.svg";
import { ReactComponent as ReqNRollIcon } from "../../../../../../../images/test-automation/framework_reqnrollIcon.svg";
import { ReactComponent as BehaveIcon } from "../../../../../../../images/test-automation/framework_behaveIcon.svg";
import { ReactComponent as MochaIcon } from "../../../../../../../images/test-automation/framework_mochaIcon.svg";
import { ReactComponent as NewProjectIcon } from "../../../../../../../images/test-automation/projectType_newProject.svg";
import { ReactComponent as ExistingProjectIcon } from "../../../../../../../images/test-automation/projectType_existingProject.svg";
import { ReactComponent as NewTestCaseIcon } from "../../../../../../../images/test-automation/testCaseType_newTestCase.svg";
import { ReactComponent as ModifiedTestCaseIcon } from "../../../../../../../images/test-automation/testCaseType_modifiedTestCase.svg";
import { ReactComponent as JavaIcon } from "../../../../../../../images/test-automation/language_javaIcon.svg";
import { ReactComponent as JavaScriptIcon } from "../../../../../../../images/test-automation/language_javascriptIcon.svg";
import { ReactComponent as TypeScriptIcon } from "../../../../../../../images/test-automation/language_typescript.svg";
import { ReactComponent as CSharpIcon } from "../../../../../../../images/test-automation/language_CSharpIcon.svg";
import { ReactComponent as PythonIcon } from "../../../../../../../images/test-automation/language_pythonIcon.svg";

// Redux imports
import { useDispatch, useSelector } from "react-redux";
import {
  createAutomationPreferences,
  resetAutomationPreferences,
  setAutomationToolState,
  setFrameworkState,
  setProgrammingLanguageState,
  setProjectTypeState,
  setTestCaseTypeState,
} from "../../../../../../../store/slices/test-automation/web/flow-stages/automationPreferencesSlice";


const ConfigurePreferencesPage = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  // Capture state variables
  const automationType = location.state?.automationType || "web";
  const locationKey = location.state?.locationKey;

  // capture data fromm redux store
  const selectedTools = useSelector(
    (state) => state["testAutomation"][locationKey]["webAutomation"]["automationPreferences"] || {}
  );

  useEffect(() => {
    // Validation for locationKey
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }

    // creating automation preferences in redux store
    if (Object.keys(selectedTools).length === 0) {
      dispatch(createAutomationPreferences(locationKey));
    }
  }, [locationKey, selectedTools]);

  // Configuration mapping for tool dependencies
  const toolDependencies = {
    playwright: {
      languages: ["typescript", "csharp"],
      frameworks: {
        typescript: ["mocha"],
        csharp: ["specflow", "reqnroll"],
      },
    },
    selenium: {
      // languages: ["java", "javascript", "csharp", "python"],
      languages: [],
      frameworks: {
        java: ["cucumber"],
        javascript: ["mocha"],
        csharp: ["specflow"],
        python: ["behave"],
      },
    },
    cypress: {
      // languages: ["javascript", "typescript"],
      languages: [],
      frameworks: {
        javascript: ["mocha"],
        typescript: ["mocha"],
      },
    },
    robot: {
      // languages: ["python"],
      languages: [],
      frameworks: {
        python: ["behave"],
      },
    },
  };

  const automationTools = [
    { id: "selenium", name: "Selenium", icon: <SeleniumIcon /> },
    { id: "playwright", name: "Playwright", icon: <PlaywrightIcon /> },
    { id: "cypress", name: "Cypress", icon: <CypressIcon /> },
    { id: "robot", name: "Robot Framework", icon: <RobotIcon /> },
  ];

  const allProgrammingLanguages = [
    { id: "typescript", name: "TypeScript", icon: <TypeScriptIcon /> },
    { id: "csharp", name: "C#", icon: <CSharpIcon /> },
    { id: "java", name: "Java", icon: <JavaIcon /> },
    { id: "javascript", name: "JavaScript", icon: <JavaScriptIcon /> },
    { id: "python", name: "Python", icon: <PythonIcon /> },
  ];

  const allFrameworks = [
    { id: "specflow", name: "SpecFlow", icon: <SpecFlowIcon /> },
    { id: "reqnroll", name: "Req N Roll", icon: <ReqNRollIcon /> },
    { id: "cucumber", name: "Cucumber", icon: <CucumberIcon /> },
    { id: "mocha", name: "Mocha", icon: <MochaIcon /> },
    { id: "behave", name: "Behave", icon: <BehaveIcon /> },
  ];

  const codeStyleOptions = ["Page Object Model (POM)"];

  const testCaseTypes = [
    {
      id: "nt",
      name: "New Test Case",
      icon: <NewTestCaseIcon />,
      description: "Create new test cases",
    },
    {
      id: "et",
      name: "Modified Test Case",
      icon: <ModifiedTestCaseIcon />,
      description: "Modify existing test cases",
    },
  ];

  const projectTypes = [
    {
      id: "np",
      name: "New Project",
      icon: <NewProjectIcon />,
      description: "Create a new project",
    },
    {
      id: "ep",
      name: "Existing Project",
      icon: <ExistingProjectIcon />,
      description: "Use existing project",
    },
  ];

  // Get ordered programming languages - enabled first, then disabled
  const getOrderedLanguages = () => {
    if (!selectedTools.automationTool) {
      return allProgrammingLanguages; // Show all as disabled
    }

    const toolConfig = toolDependencies[selectedTools.automationTool];
    if (!toolConfig) return allProgrammingLanguages;

    const enabledLanguageIds = toolConfig.languages;

    // Sort: enabled languages first, maintaining original order
    const enabled = allProgrammingLanguages.filter((lang) =>
      enabledLanguageIds.includes(lang.id)
    );
    const disabled = allProgrammingLanguages.filter(
      (lang) => !enabledLanguageIds.includes(lang.id)
    );

    return [...enabled, ...disabled];
  };

  // Get ordered frameworks - enabled first, then disabled
  const getOrderedFrameworks = () => {
    if (!selectedTools.automationTool || !selectedTools.programmingLanguage) {
      return allFrameworks; // Show all as disabled
    }

    const toolConfig = toolDependencies[selectedTools.automationTool];
    if (
      !toolConfig ||
      !toolConfig.frameworks[selectedTools.programmingLanguage]
    ) {
      return allFrameworks; // Show all as disabled
    }

    const enabledFrameworkIds =
      toolConfig.frameworks[selectedTools.programmingLanguage];

    // Sort: enabled frameworks first, maintaining original order
    const enabled = allFrameworks.filter((framework) =>
      enabledFrameworkIds.includes(framework.id)
    );
    const disabled = allFrameworks.filter(
      (framework) => !enabledFrameworkIds.includes(framework.id)
    );

    return [...enabled, ...disabled];
  };

  // Check if a language is enabled
  const isLanguageEnabled = (languageId) => {
    if (!selectedTools.automationTool) return false;

    const toolConfig = toolDependencies[selectedTools.automationTool];
    return toolConfig?.languages.includes(languageId) || false;
  };

  // Check if a framework is enabled
  const isFrameworkEnabled = (frameworkId) => {
    if (!selectedTools.automationTool || !selectedTools.programmingLanguage)
      return false;

    const toolConfig = toolDependencies[selectedTools.automationTool];
    return (
      toolConfig?.frameworks[selectedTools.programmingLanguage]?.includes(
        frameworkId
      ) || false
    );
  };

  const handleToolSelect = (toolType, toolId) => {
    // Updating data for seleted tools in store using dispatch
    switch (toolType) {
      case "automationTool":
        dispatch(setAutomationToolState(locationKey, toolId));
        dispatch(setProgrammingLanguageState(locationKey, ""));
        dispatch(setFrameworkState(locationKey, ""));
        break;

      case "programmingLanguage":
        dispatch(setProgrammingLanguageState(locationKey, toolId));
        dispatch(setFrameworkState(locationKey, ""));
        break;

      case "framework":
        dispatch(setFrameworkState(locationKey, toolId));
        break;

      case "testCaseType":
        dispatch(setTestCaseTypeState(locationKey, toolId));
        dispatch(setProjectTypeState(locationKey, ""));
        break;

      case "projectType":
        dispatch(setProjectTypeState(locationKey, toolId));
        break;

      default:
        break;
    }
  };

  // Handle function for next step
  const handleNext = () => {
    navigate("/dashboard/test-automation/set-environment", {
        automationType,
        preferences: selectedTools,
    });
  };

  // Handle function for back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Handle function for reseting preferences
  const handleReset = () => {
    dispatch(resetAutomationPreferences(locationKey));
  };

  const renderCard = (item, toolType, isSelected, isEnabled = true) => (
    <Card
      key={item.id}
      sx={{
        cursor: isEnabled ? "pointer" : "not-allowed",
        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: "8px",
        transition: "all 0.2s ease",
        "&:hover": isEnabled
          ? {
              borderColor: "#1976d2",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }
          : {},
        backgroundColor: isSelected
          ? "#e6f2ff"
          : isEnabled
          ? "white"
          : "#f5f5f5",
        width: "251.5px",
        height: "106px",
        margin: "0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        opacity: isEnabled ? 1 : 0.5,
      }}
      onClick={
        isEnabled ? () => handleToolSelect(toolType, item.id) : undefined
      }
    >
      <CardContent
        sx={{
          p: "16px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "16px",
          pt: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            opacity: isEnabled ? 1 : 0.5,
          }}
        >
          {item.icon}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Manrope",
            fontWeight: 500,
            fontStyle: "Medium",
            fontSize: "16px",
            color: isEnabled ? "#000000" : "#999999",
            textAlign: "left",
          }}
        >
          {item.name}
        </Typography>
      </CardContent>
    </Card>
  );

  const renderTestCaseCard = (item, toolType, isSelected, isEnabled = true) => (
    <Card
      key={item.id}
      sx={{
        cursor: isEnabled ? "pointer" : "not-allowed",
        border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
        borderRadius: "8px",
        transition: "all 0.2s ease",
        "&:hover": isEnabled
          ? {
              borderColor: "#1976d2",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }
          : {},
        backgroundColor: isSelected
          ? "#e6f2ff"
          : isEnabled
          ? "white"
          : "#f5f5f5",
        width: "530px",
        height: "106px",
        margin: "0 auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        opacity: isEnabled ? 1 : 0.5,
        gap: "16px",
      }}
      onClick={
        isEnabled ? () => handleToolSelect(toolType, item.id) : undefined
      }
    >
      <CardContent
        sx={{
          p: "16px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "16px",
          pt: "12px",
        }}
      >
        <Box
          sx={{
            fontSize: "32px",
            color: "#1976d2",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          {item.icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Manrope",
            fontWeight: 500,
            fontStyle: "Medium",
            fontSize: "16px",
            color: "#000000",
            textAlign: "left",
          }}
        >
          {item.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#666", textAlign: "left", fontFamily: "Manrope" }}
        >
          {item.description}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* White Content Area */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px 8px 0 0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            p: 4,
            flex: 1,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mb: 4,
              fontWeight: "bold",
              color: "#333",
              fontSize: "28px",
              fontFamily: "Manrope",
            }}
          >
            Configure Preferences
          </Typography>

          {/* Automation Tools/Platform Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: "#333",
                fontSize: "16px",
                fontFamily: "Manrope",
              }}
            >
              Automation Tools/Platform
            </Typography>
            <Grid2
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(250px, 1fr))",
                  md: "repeat(auto-fit, minmax(250px, 1fr))",
                  lg: "repeat(auto-fit, minmax(250px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              {automationTools.map((tool) => (
                <Grid2 item key={tool.id} xs={3}>
                  {renderCard(
                    tool,
                    "automationTool",
                    selectedTools.automationTool === tool.id,
                    true // Tools are always enabled
                  )}
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Programming Languages Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: selectedTools.automationTool ? "#333" : "#999",
                fontFamily: "Manrope",
              }}
            >
              Programming Languages
            </Typography>
            <Grid2
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(250px, 1fr))",
                  md: "repeat(auto-fit, minmax(250px, 1fr))",
                  lg: "repeat(auto-fit, minmax(250px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              {getOrderedLanguages().map((lang) => (
                <Grid2 item key={lang.id} xs={3}>
                  {renderCard(
                    lang,
                    "programmingLanguage",
                    selectedTools.programmingLanguage === lang.id,
                    isLanguageEnabled(lang.id)
                  )}
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Frameworks Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: selectedTools.programmingLanguage ? "#333" : "#999",
                fontFamily: "Manrope",
              }}
            >
              Frameworks
            </Typography>
            <Grid2
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(250px, 1fr))",
                  md: "repeat(auto-fit, minmax(250px, 1fr))",
                  lg: "repeat(auto-fit, minmax(250px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              {getOrderedFrameworks().map((framework) => (
                <Grid2 item key={framework.id} xs={3}>
                  {renderCard(
                    framework,
                    "framework",
                    selectedTools.framework === framework.id,
                    isFrameworkEnabled(framework.id)
                  )}
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Code Style Preferences Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: "#333",
                fontFamily: "Manrope",
              }}
            >
              Code Style Preferences
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: 600 }}>
              <InputLabel>Code Style</InputLabel>
              <Select
                value={selectedTools.codeStyle || ""}
                label="Code Style"
                onChange={(e) => handleToolSelect("codeStyle", e.target.value)}
                disabled
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  "& .MuiSelect-select": {
                    color: "#666",
                  },
                }}
              >
                {codeStyleOptions.map((style) => (
                  <MenuItem key={style} value={style}>
                    {style}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Test Case Type Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: "#333",
                fontFamily: "Manrope",
              }}
            >
              Test Case Type
            </Typography>
            <Grid2
              container
              spacing={3}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(500px, 1fr))",
                  md: "repeat(auto-fit, minmax(500px, 1fr))",
                  lg: "repeat(auto-fit, minmax(500px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              {testCaseTypes.map((type) => (
                <Grid2 item key={type.id} xs={6}>
                  {renderTestCaseCard(
                    type,
                    "testCaseType",
                    selectedTools.testCaseType === type.id
                  )}
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Project Type Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "600",
                color: "#333",
                fontFamily: "Manrope",
              }}
            >
              Project Type
            </Typography>
            <Grid2
              container
              spacing={3}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(auto-fit, minmax(500px, 1fr))",
                  md: "repeat(auto-fit, minmax(500px, 1fr))",
                  lg: "repeat(auto-fit, minmax(500px, 1fr))",
                },
                gap: "16px",
                width: "100%",
                maxWidth: "none",
                margin: "0 auto",
              }}
            >
              {projectTypes.map((type) => (
                <Grid2 item key={type.id} xs={6}>
                  {renderTestCaseCard(
                    type,
                    "projectType",
                    selectedTools.projectType === type.id,
                    selectedTools.testCaseType !== "et" ? true : type.id !== "np"
                  )}
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Box>

        {/* Footer Area */}
        <Box
          sx={{
            mt: 4,
            backgroundColor: "transparent",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Left: Back Button */}
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
              sx={{
                // width: "120px",
                // height: "40px",
                borderColor: "#0094FB",
                color: "#0094FB",
                textTransform: "none",
                fontFamily: "Manrope",
                fontWeight: 500,
                borderRadius: "8px",
                borderWidth: "2px",
                paddingTop: "8px",
                paddingRight: "16px",
                paddingBottom: "8px",
                paddingLeft: "16px",
                // gap: "8px",
                "&:hover": {
                  borderColor: "#0094FB",
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              Back
            </Button>

            {/* Right: Reset and Next Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ResetIcon />}
                onClick={handleReset}
                sx={{
                  // width: "199px",
                  // height: "40px",
                  borderColor: "#0094FB",
                  color: "#0094FB",
                  textTransform: "none",
                  fontFamily: "Manrope",
                  fontWeight: 500,
                  borderRadius: "8px",
                  borderWidth: "2px",
                  paddingTop: "8px",
                  paddingRight: "16px",
                  paddingBottom: "8px",
                  paddingLeft: "16px",
                  // gap: "8px",
                  "&:hover": {
                    borderColor: "#0094FB",
                    backgroundColor: "rgba(0, 148, 251, 0.04)",
                  },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                disabled={
                  !selectedTools.automationTool ||
                  !selectedTools.programmingLanguage ||
                  !selectedTools.framework ||
                  !selectedTools.testCaseType ||
                  !selectedTools.projectType
                }
                sx={{
                  // width: "199px",
                  // height: "40px",
                  backgroundColor: "#0094FB",
                  color: "white",
                  textTransform: "none",
                  fontFamily: "Manrope",
                  fontWeight: 500,
                  borderRadius: "8px",
                  borderWidth: "2px",
                  borderColor: "#0094FB",
                  paddingTop: "8px",
                  paddingRight: "16px",
                  paddingBottom: "8px",
                  paddingLeft: "16px",
                  // gap: "8px",
                  "&:hover": {
                    backgroundColor: "#0078D4",
                    borderColor: "#0078D4",
                  },
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#666",
                    borderColor: "#ccc",
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ConfigurePreferencesPage;
